/**
 * `THEME_CATALOG` — the single source of truth for user-selectable themes.
 *
 * Remaster U1 (2026-07-04): ThemeSettings and the CommandPalette each carried
 * their own hardcoded theme list and they had drifted apart — the palette
 * offered five hidden legacy themes and omitted the three published ones.
 * Both now render from this catalog; add or expose a theme in exactly one place.
 *
 * The CSS for every entry lives in styles/tokens.css as a
 * `:root[data-theme='…']` block.
 *
 * Remaster U5 (2026-07-04): the seven parked legacy themes that used to sit
 * here as comments (light, paper, oled, forest, slate_light, lunar,
 * ocean_floor) were DELETED outright — tokens.css blocks removed, `Theme`
 * union shrunk, and useUiStore's loadTheme() migrates any persisted legacy
 * value to its nearest survivor. The catalog below IS the complete theme set.
 */
import type { Theme } from '@/stores';

export interface ThemeCatalogEntry {
  id: Theme;
  label: string;
  hint: string;
}

export const THEME_CATALOG: readonly ThemeCatalogEntry[] = [
  // 2026-05-30 redesign — the three published themes.
  { id: 'ember',       label: 'Ember',       hint: 'Default — warm near-black + ember orange.' },
  { id: 'blue-slate',  label: 'Blue Slate',  hint: 'Cool neutral — azure accent, elevation via shadow.' },
  { id: 'parchment',   label: 'Parchment',   hint: 'Light, paper-warm — ink red-orange accent.' },
  // Slate restored 2026-05-31 (Chris ask) — the v2 Slate+Gold palette.
  { id: 'slate',       label: 'Slate',       hint: 'Slate + nixie orange + liquid-oxygen blue.' },
  // Amber/green CRT terminal themes (2026-06-09).
  { id: 'terminal-yellow', label: 'Terminal Yellow', hint: 'Amber-phosphor CRT — monochrome amber on dark glass.' },
  { id: 'terminal-green',  label: 'Terminal Green',  hint: 'Green-phosphor CRT — classic green terminal.' },
  { id: 'terminal-nixie',  label: 'Terminal Nixie',  hint: 'Nixie-tube reddish-orange CRT.' },
  { id: 'system', label: 'System', hint: 'Follow the OS preference (Ember on dark, Parchment on light).' },
];
